'use client';

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  MapPin,
  Calendar,
  Search,
  ChevronRight,
  ChevronLeft,
  CircleCheck,
  CircleAlert,
  CircleMinus,
  LayoutGrid,
  Map,
  Loader2,
  X,
} from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import { Input } from '@/components/ui/input/input';
import { Flag } from '@/components/ui/flag/flag';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useRegional } from '@/components/providers/regional-provider';
import type { MapboxMap, MapboxMarker } from '@/types/mapbox';

// Public Mapbox token for public-facing pages (safe to expose in client)
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || '';

type RideStatus = 'open' | 'almost_full' | 'at_capacity';

interface Ride {
  id: string;
  city: string;
  slug: string;
  state: string;
  stateCode: string | null;
  date: string;
  time: string | null;
  participants: number;
  capacity: number | null;
  capacityLabel: string | null;
  status: RideStatus;
  coordinates: string | null;
  raised: number;
  isPostponed: boolean;
  isCancelled: boolean;
  postponedDate: string | null;
}

type ViewMode = 'grid' | 'map';

interface CountryOption {
  code: string;
  name: string;
}

interface RideByCountryRow {
  id: string;
  name: string;
  slug: string;
  stateCode: string | null;
  stateName: string | null;
  eventDate: string | null;
  startTime: string | null;
  capacity: string | null;
  currentRegistrations: number;
  coordinates: string | null;
  raised: number;
  donationCount: number;
  currencyCode: string;
  isPostponed?: boolean;
  isCancelled?: boolean;
  postponedDate?: string | null;
}

interface CountryDataView {
  name: string;
  slug: string;
  code: string;
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseCapacity(capacity: string | null): number | null {
  if (!capacity) return null;
  const parsed = Number(capacity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toRideStatus(participants: number, capacity: number | null): RideStatus {
  if (!capacity || capacity <= 0) return 'open';
  const occupancyRatio = participants / capacity;
  if (occupancyRatio >= 1) return 'at_capacity';
  if (occupancyRatio >= 0.8) return 'almost_full';
  return 'open';
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatEventDate(dateIso: string | null): string {
  if (!dateIso) return 'TBC';
  // Extract date parts directly — never create a Date object to avoid
  // timezone shift (UTC midnight → previous day in UTC-negative timezones).
  const match = dateIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return 'TBC';
  const [, yearStr, monthStr, dayStr] = match;
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (month < 1 || month > 12 || day < 1 || day > 31) return 'TBC';
  return `${MONTH_NAMES[month - 1]} ${day}, ${yearStr}`;
}

function formatPostponedDateLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const formatted = formatEventDate(value);
  return formatted === 'TBC' ? null : formatted;
}

function formatEventTime(time: string | null): string | null {
  if (!time) return null;
  const [hourString, minuteString] = time.split(':');
  const hour = Number(hourString);
  const minute = Number(minuteString);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time;

  const period = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

const DEFAULT_COUNTRY: CountryDataView = {
  name: 'Country',
  slug: 'country',
  code: 'XX',
};

interface CountryData {
  name: string;
  slug: string;
  code: string;
  rides: Ride[];
}

// Helper function to get status icon and styling
const getStatusIcon = (status: RideStatus, t: (key: string) => string) => {
  switch (status) {
    case 'open':
      return {
        label: t('statusAvailable'),
        icon: CircleCheck,
        className: 'text-emerald-500',
      };
    case 'almost_full':
      return {
        label: t('statusAlmostFull'),
        icon: CircleAlert,
        className: 'text-amber-500',
      };
    case 'at_capacity':
      return {
        label: t('statusFull'),
        icon: CircleMinus,
        className: 'text-orange-500',
      };
    default:
      return {
        label: t('statusAvailable'),
        icon: CircleCheck,
        className: 'text-emerald-500',
      };
  }
};

interface CountryEventsPageProps {
  countrySlug?: string;
  prefetchedData?: {
    countries: CountryOption[];
    rides: RideByCountryRow[];
    countryCode: string;
    currencyCode: string;
  };
}

export function CountryEventsPage({
  countrySlug = 'australia',
  prefetchedData,
}: CountryEventsPageProps) {
  const t = useTranslations('events');
  const format = useFormatter();
  const { currency: regionalCurrency } = useRegional();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchOpen, setSearchOpen] = useState(false);

  // Map refs and state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxMap | null>(null);
  const markersRef = useRef<{ marker: MapboxMarker; element: HTMLElement; ride: Ride }[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  const { data: countriesData = [] } = trpc.publicLookups.countries.useQuery(undefined, {
    ...(prefetchedData?.countries && { initialData: prefetchedData.countries as never }),
  });

  const selectedCountry = useMemo<CountryDataView | null>(() => {
    const match = (countriesData as CountryOption[]).find(
      (country) => toSlug(country.name) === countrySlug,
    );
    if (!match) return null;
    return {
      name: match.name,
      slug: countrySlug,
      code: match.code,
    };
  }, [countriesData, countrySlug]);

  const ridesInitialData =
    prefetchedData?.rides &&
    prefetchedData.currencyCode === regionalCurrency &&
    prefetchedData.countryCode === selectedCountry?.code
      ? prefetchedData.rides
      : undefined;

  const { data: ridesData = [], isLoading: isLoadingRides } =
    trpc.publicLookups.ridesByCountry.useQuery(
      { countryCode: selectedCountry?.code ?? '', currencyCode: regionalCurrency },
      {
        enabled: !!selectedCountry?.code,
        ...(ridesInitialData && { initialData: ridesInitialData }),
      },
    );

  const countryData = useMemo<CountryData>(
    () => ({
      name:
        selectedCountry?.name ??
        countrySlug.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
      slug: countrySlug,
      code: selectedCountry?.code ?? DEFAULT_COUNTRY.code,
      rides: (ridesData as RideByCountryRow[]).map((ride) => {
        const participants = Number(ride.currentRegistrations ?? 0);
        const capacity = parseCapacity(ride.capacity);
        const isPostponed = ride.isPostponed ?? false;
        const isCancelled = ride.isCancelled ?? false;
        const postponedDateLabel = formatPostponedDateLabel(ride.postponedDate);
        // Bake the displayed label into `date` so consumers (card, popup, list)
        // pick up the right status text without per-site conditionals.
        const displayDate = isCancelled
          ? 'Cancelled'
          : isPostponed
            ? postponedDateLabel
              ? `Postponed to ${postponedDateLabel}`
              : 'Postponed'
            : formatEventDate(ride.eventDate);

        return {
          id: ride.id,
          city: ride.name,
          slug: ride.slug,
          state: ride.stateName ?? '',
          stateCode: ride.stateCode,
          date: displayDate,
          time: formatEventTime(ride.startTime),
          participants,
          capacity,
          capacityLabel: capacity
            ? `${format.number(participants)}/${format.number(capacity)}`
            : null,
          status: toRideStatus(participants, capacity),
          coordinates: ride.coordinates,
          raised: Number(ride.raised ?? 0),
          isPostponed,
          isCancelled,
          postponedDate: ride.postponedDate ?? null,
        };
      }),
    }),
    [countrySlug, format, ridesData, selectedCountry],
  );

  const availableStates = useMemo(() => {
    const states = [...new Set(countryData.rides.map((ride) => ride.state).filter(Boolean))];
    return states.sort((a, b) => a.localeCompare(b));
  }, [countryData.rides]);

  const totalParticipants = useMemo(
    () => countryData.rides.reduce((sum, ride) => sum + ride.participants, 0),
    [countryData.rides],
  );

  const totalRaised = useMemo(
    () => countryData.rides.reduce((sum, ride) => sum + ride.raised, 0),
    [countryData.rides],
  );

  // Filter rides
  const filteredRides = useMemo(() => {
    let filtered = countryData.rides;

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.city.toLowerCase().includes(lowerQuery) || r.state.toLowerCase().includes(lowerQuery),
      );
    }

    if (selectedState) {
      filtered = filtered.filter((ride) => ride.state === selectedState);
    }

    return filtered.sort((a, b) => a.city.localeCompare(b.city));
  }, [countryData.rides, searchQuery, selectedState]);

  // Parse coordinates from string "lat,lng"
  const parseCoordinates = useCallback(
    (coords: string | null | undefined): { lat: number; lng: number } | null => {
      if (!coords) return null;
      const parts = coords.split(',').map((s) => s.trim());
      if (parts.length !== 2) return null;
      const [lat, lng] = parts.map(Number);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    },
    [],
  );

  // Filter rides with valid coordinates for map
  const ridesWithCoords = useMemo(() => {
    return filteredRides.filter((ride) => {
      const coords = parseCoordinates(ride.coordinates);
      return coords !== null;
    });
  }, [filteredRides, parseCoordinates]);

  // Load Mapbox GL JS
  useEffect(() => {
    if (viewMode !== 'map' || !MAPBOX_TOKEN) return;

    // Check if mapbox-gl is already loaded
    if (window.mapboxgl) {
      queueMicrotask(() => setMapLoaded(true));
      return;
    }

    // Load Mapbox GL JS CSS
    const link = document.createElement('link');
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Load Mapbox GL JS
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.async = true;
    script.onload = () => {
      setMapLoaded(true);
    };
    document.head.appendChild(script);
  }, [viewMode]);

  // Initialize map
  useEffect(() => {
    if (viewMode !== 'map' || !mapLoaded || !mapContainer.current || !MAPBOX_TOKEN) return;
    if (map.current) return; // Map already initialized

    const mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Calculate center from rides or use country center
    const ridesCoords = ridesWithCoords
      .map((r) => parseCoordinates(r.coordinates))
      .filter((c): c is { lat: number; lng: number } => c !== null);

    let center: [number, number] = [0, 20];
    const zoom = 4;

    if (ridesCoords.length > 0) {
      const avgLat = ridesCoords.reduce((sum, c) => sum + c.lat, 0) / ridesCoords.length;
      const avgLng = ridesCoords.reduce((sum, c) => sum + c.lng, 0) / ridesCoords.length;
      center = [avgLng, avgLat];
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: center,
      zoom: zoom,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Wait for map to load before adding markers
    map.current.on('load', () => {
      setMapInitialized(true);
    });

    return () => {
      // Clean up markers
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];

      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setMapInitialized(false);
    };
  }, [viewMode, mapLoaded, parseCoordinates, ridesWithCoords]);

  // Add markers when map is initialized or rides change
  useEffect(() => {
    if (viewMode !== 'map' || !mapInitialized || !map.current) return;

    const mapboxgl = window.mapboxgl;

    // Clear existing markers
    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];

    if (ridesWithCoords.length === 0) return;

    // Create bounds to fit all markers
    const bounds = new mapboxgl.LngLatBounds();

    // Status color config
    const statusColors: Record<RideStatus, string> = {
      open: '#22c55e',
      almost_full: '#f59e0b',
      at_capacity: '#f97316',
    };

    // Add markers for each ride
    ridesWithCoords.forEach((ride) => {
      const coords = parseCoordinates(ride.coordinates);
      if (!coords) return;

      const statusColor = statusColors[ride.status];
      const lngLat: [number, number] = [coords.lng, coords.lat];

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'ride-marker';
      el.style.cssText = `
        width: 28px;
        height: 28px;
        background-color: ${statusColor};
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        transition: box-shadow 0.2s ease;
      `;

      el.addEventListener('mouseenter', () => {
        el.style.boxShadow = '0 0 0 4px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.3)';
      });
      el.addEventListener('mouseleave', () => {
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
      });

      // Status label
      const statusLabels: Record<RideStatus, string> = {
        open: t('statusAvailable'),
        almost_full: t('statusAlmostFull'),
        at_capacity: t('statusFull'),
      };

      // Create popup
      const raisedFormatted = format.number(ride.raised, {
        style: 'currency',
        currency: regionalCurrency,
        notation: 'compact',
        maximumFractionDigits: 0,
      });

      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div style="padding: 12px; min-width: 220px; font-family: system-ui, sans-serif;">
          <h3 style="font-weight: 600; margin: 0 0 4px 0; font-size: 16px; color: #1c1917;">${ride.city}</h3>
          <p style="margin: 0 0 12px 0; font-size: 13px; color: #78716c;">
            ${ride.state}
          </p>
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
            <span style="
              display: inline-flex;
              align-items: center;
              padding: 4px 10px;
              border-radius: 9999px;
              font-size: 11px;
              font-weight: 500;
              background-color: ${statusColor}20;
              color: ${statusColor};
            ">${statusLabels[ride.status]}</span>
          </div>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 12px; font-size: 12px;">
            <div>
              <p style="margin: 0; color: #78716c;">${t('riders')}</p>
              <p style="margin: 0; font-weight: 600; color: #1c1917;">${format.number(ride.participants)}</p>
            </div>
            <div>
              <p style="margin: 0; color: #78716c;">${t('raised')}</p>
              <p style="margin: 0; font-weight: 600; color: hsl(var(--dgr-gold-hover));">${raisedFormatted}</p>
            </div>
          </div>
          <a href="/events/${countryData.slug}/${ride.slug}" style="
            display: block;
            text-align: center;
            padding: 8px 16px;
            background: #1c1917;
            color: white;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            text-decoration: none;
          ">${t('viewEvent')}</a>
        </div>
      `);

      // Create marker
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat(lngLat)
        .setPopup(popup)
        .addTo(map.current!);

      markersRef.current.push({ marker, element: el, ride });
      bounds.extend(lngLat);
    });

    // Fit map to bounds if we have markers
    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds.toArray(), {
        padding: 60,
        maxZoom: 10,
      });
    }
  }, [
    viewMode,
    mapInitialized,
    ridesWithCoords,
    parseCoordinates,
    countryData.slug,
    format,
    regionalCurrency,
    t,
  ]);

  return (
    <>
      <main className="flex-1">
        {/* Hero / Cover Section */}
        <section className="relative h-[360px] md:h-[480px]">
          <Image
            src="/images/dgr/HeroImage.jpg"
            alt={countryData.name}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

          {/* Hero Content */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Link
                href="/events"
                className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-6 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('allCountries')}
              </Link>
              <div className="flex flex-col items-center gap-4 mb-4">
                <h1 className="text-4xl md:text-5xl font-bold text-white">{countryData.name}</h1>
              </div>
            </div>
          </div>
        </section>

        {/* Country Stats */}
        <section className="border-b border-stone-200 bg-stone-50">
          <div className="container max-w-6xl py-6">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-dgr-gold-hover">
                  {format.number(totalRaised, {
                    style: 'currency',
                    currency: regionalCurrency,
                    notation: 'compact',
                    maximumFractionDigits: 1,
                    trailingZeroDisplay: 'stripIfInteger',
                  })}
                </p>
                <p className="text-stone-500 text-sm">{t('totalRaised')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {format.number(totalParticipants)}
                </p>
                <p className="text-stone-500 text-sm">{t('participants')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {countryData.rides.length}
                </p>
                <p className="text-stone-500 text-sm">{t('rides')}</p>
              </div>
              <div className="text-center hidden md:block">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {availableStates.length}
                </p>
                <p className="text-stone-500 text-sm">{t('regions')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Rides Content */}
        <section className="py-8 bg-dgr-cream">
          <div className="container max-w-6xl">
            {/* Filters and View Toggle */}
            <div className="flex items-center justify-between gap-2 mb-8">
              <div className="flex items-center gap-2">
                {/* State Filter with Flag */}
                {availableStates.length > 1 ? (
                  <div className="flex items-center">
                    {/* Flag prepend */}
                    <div className="flex items-center justify-center h-10 px-3 bg-stone-100 border border-r-0 border-stone-300 rounded-l-md">
                      <Flag code={countryData.code} size="sm" />
                    </div>
                    {/* Region Select */}
                    <Select
                      value={selectedState || 'all'}
                      onValueChange={(value) => setSelectedState(value === 'all' ? null : value)}
                    >
                      <SelectTrigger className="w-[120px] sm:w-[160px] h-10 bg-white border-stone-300 rounded-l-none">
                        <SelectValue placeholder={t('allRegions')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allRegions')}</SelectItem>
                        {availableStates.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  /* Flag only - display when no regions to filter */
                  <div className="flex items-center justify-center h-10 px-3 bg-stone-100 border border-stone-300 rounded-md">
                    <Flag code={countryData.code} size="sm" />
                  </div>
                )}

                {/* Search - Desktop */}
                <div className="relative hidden sm:block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    placeholder={t('searchCities')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 pl-10 w-[200px] bg-white border-stone-300 text-stone-900 placeholder:text-stone-400"
                  />
                </div>

                {/* Search - Mobile Popover */}
                <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        'sm:hidden flex items-center justify-center w-10 h-10 rounded-lg border transition-colors',
                        searchQuery
                          ? 'bg-stone-900 border-stone-900 text-white'
                          : 'bg-white border-stone-300 text-stone-500 hover:bg-stone-50',
                      )}
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[calc(100vw-2rem)] p-2" align="start">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        placeholder={t('searchCities')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-10 pl-10 pr-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400"
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* View Toggle - Dropdown */}
              <Select value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
                <SelectTrigger className="w-[110px] sm:w-[130px] h-10 bg-white border-stone-300">
                  {viewMode === 'grid' ? (
                    <LayoutGrid className="w-4 h-4 mr-1.5 text-stone-500 flex-shrink-0" />
                  ) : (
                    <Map className="w-4 h-4 mr-1.5 text-stone-500 flex-shrink-0" />
                  )}
                  <span className="hidden sm:inline truncate">
                    {viewMode === 'grid' ? t('gridView') : t('mapView')}
                  </span>
                </SelectTrigger>
                <SelectContent className="w-[140px]">
                  <SelectItem value="grid">
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <LayoutGrid className="w-4 h-4 flex-shrink-0" />
                      {t('gridView')}
                    </span>
                  </SelectItem>
                  <SelectItem value="map">
                    <span className="flex items-center gap-2 whitespace-nowrap">
                      <Map className="w-4 h-4 flex-shrink-0" />
                      {t('mapView')}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grid View */}
            {viewMode === 'grid' && (
              <>
                {isLoadingRides ? (
                  <div className="py-16 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-dgr-gold animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredRides.map((ride) => {
                      const statusInfo = getStatusIcon(ride.status, t);
                      const StatusIcon = statusInfo.icon;

                      return (
                        <Link
                          key={ride.id}
                          href={`/events/${countryData.slug}/${ride.slug}`}
                          className="group bg-white rounded-xl border border-stone-200 hover:border-dgr-gold hover:shadow-md transition-all p-5"
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h3 className="text-lg font-semibold text-stone-900 group-hover:text-dgr-gold-hover transition-colors">
                                    {ride.city}
                                  </h3>
                                  <p className="text-sm text-stone-500">{ride.state}</p>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0 overflow-hidden">
                                  <StatusIcon
                                    className={cn('w-5 h-5 flex-shrink-0', statusInfo.className)}
                                  />
                                  <span
                                    className={cn(
                                      'text-xs font-medium whitespace-nowrap transition-all duration-300 ease-out',
                                      'max-w-0 opacity-0 group-hover:max-w-[100px] group-hover:opacity-100',
                                      statusInfo.className,
                                    )}
                                  >
                                    {ride.capacityLabel ?? statusInfo.label}
                                  </span>
                                </div>
                              </div>
                              <div
                                className={cn(
                                  'flex items-center gap-2 text-sm mt-2',
                                  ride.isCancelled
                                    ? 'text-red-600 font-medium'
                                    : ride.isPostponed
                                      ? 'text-amber-600 font-medium'
                                      : 'text-stone-600',
                                )}
                              >
                                <Calendar className="w-4 h-4" />
                                <span>{ride.date}</span>
                                {ride.time && !ride.isPostponed && !ride.isCancelled && (
                                  <>
                                    <span className="text-stone-400">•</span>
                                    <span>{ride.time}</span>
                                  </>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm mt-4">
                                <div>
                                  <p className="text-stone-500">{t('riders')}</p>
                                  <p className="font-semibold text-stone-800">
                                    {format.number(ride.participants)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-stone-500">{t('raised')}</p>
                                  <p className="font-semibold text-dgr-gold-hover">
                                    {format.number(ride.raised, {
                                      style: 'currency',
                                      currency: regionalCurrency,
                                      notation: 'compact',
                                      maximumFractionDigits: 0,
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}

                {/* Empty State */}
                {!isLoadingRides && filteredRides.length === 0 && (
                  <div className="py-16 text-center">
                    <MapPin className="w-16 h-16 text-stone-300 mx-auto mb-6" />
                    <h2 className="text-2xl font-semibold text-stone-900 mb-2">
                      {t('noRidesFound')}
                    </h2>
                    <p className="text-stone-500 max-w-md mx-auto">{t('noRidesDescription')}</p>
                  </div>
                )}
              </>
            )}

            {/* Map View */}
            {viewMode === 'map' && (
              <div className="relative rounded-xl overflow-hidden border border-stone-200">
                {/* Map Container */}
                <div ref={mapContainer} className="h-[600px] w-full" />

                {/* Loading overlay */}
                {!mapInitialized && MAPBOX_TOKEN && (
                  <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-stone-400 mx-auto mb-2" />
                      <p className="text-sm text-stone-500">{t('loadingMap')}</p>
                    </div>
                  </div>
                )}

                {/* No token message */}
                {!MAPBOX_TOKEN && (
                  <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
                    <div className="text-center">
                      <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-stone-600">Map View</h3>
                      <p className="text-sm text-stone-500 max-w-sm mx-auto mt-2">
                        Mapbox token not configured.
                      </p>
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg px-4 py-3">
                  <p className="text-xs font-semibold text-stone-500 mb-2">{t('rideStatus')}</p>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm bg-emerald-500" />
                      <span className="text-xs text-stone-600">{t('statusAvailable')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm bg-amber-500" />
                      <span className="text-xs text-stone-600">{t('statusAlmostFull')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-white shadow-sm bg-orange-500" />
                      <span className="text-xs text-stone-600">{t('statusFull')}</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-lg shadow-lg px-4 py-2">
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="font-semibold">{ridesWithCoords.length}</span>
                      <span className="text-stone-500 ml-1">on map</span>
                    </div>
                    {filteredRides.length - ridesWithCoords.length > 0 && (
                      <div className="text-stone-500">
                        <span className="font-medium">
                          {filteredRides.length - ridesWithCoords.length}
                        </span>
                        <span className="ml-1">without coordinates</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
